import React, { useState, useEffect, useRef } from 'react'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useHubStore } from '@renderer/stores/hubStore'
import { useConfigStore } from '@renderer/stores/configState'
import { useCameraStore } from '@renderer/stores/cameraStore'
import Preview from '@renderer/components/Preview'
import Button from '@renderer/components/Button'
import { SettingsDisclosure } from './SettingsDisclosure'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import {
  resolveConnectArgs,
  makeUsbSelectionValueForDevice,
  disambiguateWebcamLabels,
  disambiguateUsbCameraLabels,
  usbCameraSerialSuffix,
  WASM_GPHOTO2_V2_FLAG
} from '@renderer/utils/cameras/cameraSelection'
import { cameraDriverStatusKey } from '../../../../shared/cameraDriverState'
import { useTranslation } from 'react-i18next'
import { useDeviceManager } from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import {
  ASSUMED_UPLINK_MBPS,
  estimateCaptureFootprint,
  formatMegabytes,
  formatUploadSeconds,
  type CaptureImpactLevel
} from '@renderer/utils/captureFootprint'
import {
  CAPTURE_FORMATS,
  CAPTURE_JPEG_DEFAULT_FLAG,
  captureFormatDefault,
  normaliseCaptureFormat,
  type CaptureFormat
} from '@renderer/utils/captureEncoding'
import {
  detectAvailableSettings,
  type AvailableSettings
} from '@renderer/utils/cameras/cameraSettingsManager'
import {
  assessShutterCaptureRisks,
  detectCameraBrandFromConfig,
  type CameraBrand,
  type CaptureRisk
} from '@renderer/utils/cameras/captureRiskAssessment'
import { FLASH_SETUP_GUIDE_FLAG } from '@renderer/utils/cameras/flashFlags'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'
import { useCameraEngineBreakerStore } from '@renderer/stores/cameraEngineBreakerStore'
import { useDesignLayout } from '@renderer/hooks'
import CameraPresetsPanel from '@renderer/components/view/CameraPresetsPanel'
import {
  CameraTestShutter,
  CameraTestResult
} from '@renderer/components/view/CameraTestCapture'
import { useCameraPresetStore } from '@renderer/stores/cameraPresetStore'
import { useOfflineStore } from '@renderer/stores/offlineStore'
import {
  CAMERA_PRESETS_FLAG,
  capturePresetFromCamera,
  extractAppSettings,
  isApplyInFlight
} from '@renderer/utils/cameras/cameraPresets'
import type { ConfigNode as CameraPresetConfigNode } from '@renderer/utils/cameras/presetCompatibility'
import {
  CAMERA_PREFOCUS_FLAG,
  normaliseFocusMode,
  WAIT_FOR_FOCUS_DEFAULT,
  type FocusMode
} from '@renderer/utils/cameras/prefocus'

/**
 * English fallbacks for the per-mode hint, used when a locale has no translation yet.
 * Each one says what the operator has to do in the ROOM for that mode to be the right
 * choice — the trade-off is physical, not a software preference.
 */
const FOCUS_MODE_FALLBACK_HINTS: Record<FocusMode, string> = {
  'every-photo': 'Safest. Use this if guests stand at different distances.',
  'once-per-session':
    'Focuses once, then reuses it for the rest of the strip. Best with a floor marker so everyone stands in the same spot.',
  fixed:
    'The booth never touches focus. Only use this if you have pre-focused the lens manually (lens switch on MF).'
}

type ConfigValue = string | number | boolean
type CameraKey = 'primary' | 'secondary'

type ResizeQuality = 'pixelated' | 'low' | 'medium' | 'high'

interface PreviewQuality {
  maxWidth: number
  maxHeight: number
  quality: number
  resizeQuality: ResizeQuality
}

interface Config {
  name: string
  info: string
  label: string
  readonly: boolean
  type: 'range' | 'menu' | 'radio' | 'toggle' | 'text' | 'window' | 'section' | 'datetime'
  value?: ConfigValue
  min?: number
  max?: number
  step?: number
  choices?: string[]
  children?: Record<string, Config>
}

interface CameraSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * `modal` (default) is the standalone dialog. `panel` renders the same editor
   * inline in the Settings pane: no backdrop, no tabs, no Save/Cancel — the pane
   * above owns the primary picker and preview, essentials sit first, the rest
   * folds into an Advanced tier, and the second camera into its own.
   */
  presentation?: 'modal' | 'panel'
  /** Panel only: rendered at the top of the controls column (the pane's picker block). */
  leading?: React.ReactNode
  /** Panel only: controls beside the preview, rather than stacked. Measured by the host. */
  twoColumn?: boolean
  /** Panel only: helpers that belong with the preview (rendered under it). */
  aside?: React.ReactNode
  /**
   * Panel only: the primary slot's picker, rendered in the sticky header beside the
   * tabs. The pane owns it because detect / USB eject / refresh live there.
   */
  slotPicker?: React.ReactNode
}

/** Stable DOM anchor for a top-level camera-config section (used by the jump nav). */
const cameraConfigSectionId = (cameraKey: CameraKey, sectionName: string): string =>
  `camera-config-${cameraKey}-${sectionName}`

/** Marks the modal's scroll container so the jump nav can scroll it — and only it. */
const CAMERA_SETTINGS_SCROLL_ATTR = 'data-camera-settings-scroll'

/**
 * Scroll a config section into view inside the modal's scroll container. The camera
 * header above the content is sticky with a VARIABLE height (dropdown + live
 * preview), so a plain jump would land the section underneath it — measure the
 * header at click time and offset by it. On short viewports the header is unpinned
 * (position static) and needs no offset.
 *
 * Scrolls the modal's OWN scroll container by hand rather than calling
 * `el.scrollIntoView()`: scrollIntoView walks every scrollable ancestor, and on a
 * custom-path booth those ancestors include ScaleToFit's shell — `overflow-hidden`,
 * but still programmatically scrollable, and holding a design canvas taller than the
 * window. One jump-nav tap there scrolled the entire booth up by the overflow and
 * left it that way, putting the top chrome (reprint / settings / fullscreen padlock)
 * out of reach until the app restarted. Field report: Kabar Kamu, 2026-08-18.
 * ScaleToFit is hardened against this too — but the jump must not aim at ancestors
 * it never meant to move.
 */
const scrollToConfigSection = (cameraKey: CameraKey, sectionName: string): void => {
  const el = document.getElementById(cameraConfigSectionId(cameraKey, sectionName))
  if (!el) return
  const header = document.getElementById(`camera-settings-sticky-${cameraKey}`)
  const pinned = header && getComputedStyle(header).position === 'sticky'
  const offset = (pinned ? header.offsetHeight : 0) + 12
  const scroller = el.closest<HTMLElement>(`[${CAMERA_SETTINGS_SCROLL_ATTR}]`)
  if (!scroller) {
    // No scroller found (unexpected DOM shape): fall back to the old behavior
    // rather than leaving the jump nav dead.
    el.style.scrollMarginTop = `${offset}px`
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    return
  }
  const delta = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top
  scroller.scrollTo({ top: Math.max(0, scroller.scrollTop + delta - offset), behavior: 'smooth' })
}

/**
 * Container-query height units resolve against the MODAL box (its shell declares
 * `container-type: size`), which is the only honest reference here: `vh`/`dvh`
 * are wrong inside ScaleToFit shells (custom-path booths render the modal under a
 * CSS transform, where viewport units live in window space but layout happens in
 * design-canvas space) and drift with OS/browser zoom. Old renderers without
 * cqh support fall back to vh — correct on default-path booths, and still
 * bounded on the rest.
 */
const supportsCqh =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height', '1cqh')

/** Preview pane height budget: never more than ~28% of the modal, hard-capped at 300px. */
const PREVIEW_HEIGHT_CAP = supportsCqh ? 'min(28cqh, 300px)' : 'min(28vh, 300px)'

/**
 * Ratio-true sizing for the preview box: height ≤ PREVIEW_HEIGHT_CAP, width derived
 * from the DISPLAY aspect (what's on screen after preview rotation) and clamped to
 * the content width. Deriving width from the height cap — instead of capping height
 * directly — keeps the box exactly at the feed's aspect so `fillContainer` never
 * crops. This replaces the old width-driven sizing where a portrait ratio grew the
 * box unbounded and buried every control under the sticky header.
 */
const previewBoxStyleForRatio = (displayRatio: number): React.CSSProperties => ({
  aspectRatio: String(displayRatio),
  width: `min(100%, 24rem, calc(${PREVIEW_HEIGHT_CAP} * ${displayRatio}))`
})

/** Top-level section (by name) that contains the given widget — for warning jump links. */
const findSectionContaining = (root: Config | null, widgetName: string): string | null => {
  if (!root?.children) return null
  for (const [sectionName, section] of Object.entries(root.children)) {
    const stack: Config[] = [section]
    while (stack.length > 0) {
      const node = stack.pop() as Config
      if (node.name === widgetName) return sectionName
      if (node.children) stack.push(...Object.values(node.children))
    }
  }
  return null
}

interface QualityPreset {
  key: string
  label: string
  width: number
  height: number
  quality: number
  resizeQuality: ResizeQuality
  /**
   * Hidden from the live-preview dropdown. The preview bitmap is re-encoded on every
   * frame, so anything above 1080p costs frame rate and camera heat for pixels nobody
   * ever sees — these tiers only make sense for the saved capture.
   */
  captureOnly?: boolean
}

// Quality presets for the dropdown
const QUALITY_PRESETS: QualityPreset[] = [
  {
    key: 'pixelated',
    label: 'Pixelated (240p)',
    width: 426,
    height: 240,
    quality: 0.4,
    resizeQuality: 'pixelated'
  },
  {
    key: 'low',
    label: 'Low (480p)',
    width: 854,
    height: 480,
    quality: 0.6,
    resizeQuality: 'low'
  },
  {
    key: 'medium',
    label: 'Medium (720p)',
    width: 1280,
    height: 720,
    quality: 0.8,
    resizeQuality: 'medium'
  },
  {
    key: 'high',
    label: 'High (1080p)',
    width: 1920,
    height: 1080,
    quality: 1,
    resizeQuality: 'high'
  },
  {
    key: 'ultra',
    label: 'Ultra (1440p)',
    width: 2560,
    height: 1440,
    quality: 1,
    resizeQuality: 'high',
    captureOnly: true
  },
  {
    key: 'ultraPlus',
    label: 'Ultra+ (1800p)',
    width: 3200,
    height: 1800,
    quality: 1,
    resizeQuality: 'high',
    captureOnly: true
  },
  {
    key: 'max4k',
    label: 'Maximum (4K · 2160p)',
    width: 3840,
    height: 2160,
    quality: 1,
    resizeQuality: 'high',
    captureOnly: true
  }
]

/** Upper bound of the custom capture inputs — the 4K preset is the ceiling we support. */
const MAX_CAPTURE_WIDTH = 3840
const MAX_CAPTURE_HEIGHT = 2160

/**
 * The resize-quality dropdowns pick a downscaling algorithm, not a resolution. Listing
 * QUALITY_PRESETS there repeated the same `high` value once per high-res preset, so the
 * options are derived from the algorithm names instead.
 */
const RESIZE_QUALITY_OPTIONS: ResizeQuality[] = ['pixelated', 'low', 'medium', 'high']

const CAPTURE_IMPACT_BADGE_STYLES: Record<CaptureImpactLevel, string> = {
  light: 'bg-emerald-50 border-emerald-200 text-emerald-700',
  moderate: 'bg-amber-50 border-amber-200 text-amber-800',
  heavy: 'bg-rose-50 border-rose-200 text-rose-800'
}

/**
 * Badge + hover/focus tooltip spelling out what the selected capture resolution and file
 * format cost in filesize and upload time. Operators used to pick a preset blind; on weak
 * venue Wi-Fi a 4K choice — or a PNG one — quietly starves the upload queue, so the
 * trade-off is stated at the control.
 */
const CaptureImpactBadge: React.FC<{
  width: number
  height: number
  quality: number
  format: CaptureFormat
  tooltipId: string
}> = ({ width, height, quality, format, tooltipId }): JSX.Element => {
  const { t } = useTranslation()
  const footprint = estimateCaptureFootprint(width, height, quality, format)
  // On PNG, the same photo as JPEG is the cheapest fix there is — show what it would cost.
  const jpegFootprint =
    format === 'png' ? estimateCaptureFootprint(width, height, quality, 'jpeg') : null
  const estimateValues = {
    size: formatMegabytes(footprint.megabytes),
    seconds: formatUploadSeconds(footprint.uploadSeconds),
    mbps: ASSUMED_UPLINK_MBPS
  }

  return (
    <div className="relative mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium ${CAPTURE_IMPACT_BADGE_STYLES[footprint.level]}`}
      >
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current" />
        {t(`cameraSettings.captureImpact.level.${footprint.level}`)}
      </span>
      <span className="text-xs text-gray-600">
        {t('cameraSettings.captureImpact.estimate', estimateValues)}
      </span>
      <span className="group inline-flex">
        <button
          type="button"
          aria-describedby={tooltipId}
          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path
              fillRule="evenodd"
              d="M18 10A8 8 0 112 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <span className="sr-only">{t('cameraSettings.captureImpact.infoLabel')}</span>
        </button>
        <span
          role="tooltip"
          id={tooltipId}
          className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-72 max-w-full rounded-md bg-gray-900 p-3 text-left text-xs leading-relaxed text-white opacity-0 shadow-lg transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
        >
          <span className="block font-semibold">{t('cameraSettings.captureImpact.infoTitle')}</span>
          <span className="mt-1 block text-gray-200">
            {t('cameraSettings.captureImpact.infoBody', { mbps: ASSUMED_UPLINK_MBPS })}
          </span>
        </span>
      </span>
      {jpegFootprint && (
        <span className="w-full text-xs text-gray-500">
          {t('cameraSettings.captureImpact.jpegComparison', {
            size: formatMegabytes(jpegFootprint.megabytes)
          })}
        </span>
      )}
      {footprint.level === 'heavy' && (
        <p className="mt-1 w-full border-l-4 border-amber-400 bg-amber-50 p-3 text-xs text-amber-800">
          {jpegFootprint && jpegFootprint.level !== 'heavy'
            ? t('cameraSettings.captureImpact.heavyWarningPng')
            : t('cameraSettings.captureImpact.heavyWarning')}
        </p>
      )}
    </div>
  )
}

const RATIO_OPTIONS = [
  { label: 'Auto', value: 'auto', key: 'auto' },
  { label: 'Square (1:1)', value: 1 / 1, key: 'square' },
  { label: 'Portrait (3:4)', value: 3 / 4, key: 'portrait' },
  { label: 'Landscape (4:3)', value: 4 / 3, key: 'landscape' },
  { label: 'Wide Portrait (9:16)', value: 9 / 16, key: 'widePortrait' },
  { label: 'Wide Landscape (16:9)', value: 16 / 9, key: 'wideLandscape' },
  { label: 'Classic Portrait (2:3)', value: 2 / 3, key: 'classicPortrait' },
  { label: 'Classic Landscape (3:2)', value: 3 / 2, key: 'classicLandscape' }
]

const CameraConfigWidget: React.FC<{
  config: Config
  onChange: (name: string, value: ConfigValue) => void
  /** `row` (the settings panel): title left, control right, no boxes. */
  layout?: 'stacked' | 'row'
}> = ({ config, onChange, layout = 'stacked' }): JSX.Element | null => {
  const { t } = useTranslation()
  const { label, name, type, value, readonly, min, max, step, choices } = config
  const row = layout === 'row'
  const wrapClass = row ? 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 py-2' : ''
  const labelClass = row ? 'min-w-0 text-sm text-gray-800' : 'block text-sm font-medium mb-2 text-gray-700'
  const controlClass = row
    ? 'w-56 max-w-full justify-self-end rounded-md border border-gray-300 p-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
    : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const handleChange = (newValue: ConfigValue): void => {
    onChange(name, newValue)
  }

  switch (type) {
    case 'range':
      return (
        <div className={wrapClass}>
          <label className={labelClass}>
            {t('cameraConfig.label', label)}
          </label>
          <input
            type="number"
            value={value as number}
            min={min}
            max={max}
            step={step}
            onChange={(e) => handleChange(parseFloat(e.target.value))}
            disabled={readonly}
            className={controlClass}
            title={t('cameraConfig.label', label)}
            aria-label={t('cameraConfig.label', label)}
          />
        </div>
      )

    case 'menu':
    case 'radio':
      return (
        <div className={wrapClass}>
          <label className={labelClass}>{label}</label>
          <select
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            disabled={readonly}
            className={controlClass}
            title={label}
            aria-label={label}
          >
            {choices?.map((choice, index) => (
              <option key={`${name}-${choice}-${index}`} value={choice}>
                {choice}
              </option>
            ))}
          </select>
        </div>
      )

    case 'toggle':
      return (
        <div className={wrapClass}>
          <label className={labelClass}>{label}</label>
          <div className={row ? 'contents' : 'flex items-center justify-between p-3 border border-gray-200 rounded-md'}>
            {!row && <span className="text-sm">{label}</span>}
            <label className={row ? 'relative inline-flex cursor-pointer items-center justify-self-end' : 'relative inline-flex items-center cursor-pointer'}>
              <input
                type="checkbox"
                className="sr-only peer"
                checked={value as boolean}
                onChange={(e) => handleChange(e.target.checked)}
                disabled={readonly}
                aria-label={`Toggle ${label}`}
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
            </label>
          </div>
        </div>
      )

    case 'text':
      return (
        <div className={wrapClass}>
          <label className={labelClass}>{label}</label>
          <input
            type="text"
            value={value as string}
            onChange={(e) => handleChange(e.target.value)}
            disabled={readonly}
            className={controlClass}
            title={label}
            aria-label={label}
          />
        </div>
      )

    case 'window':
    case 'section':
      if (!config.children) return null
      return (
        <div className={row ? 'pt-2' : 'space-y-4'}>
          <h3 className={row ? 'text-sm font-semibold text-gray-900' : 'text-lg font-semibold'}>{label}</h3>
          <div className={row ? 'divide-y divide-gray-100' : 'space-y-4 pl-4'}>
            {Object.entries(config.children).map(([childName, childConfig]) => (
              <CameraConfigWidget
                key={`${name}-${childName}`}
                config={childConfig}
                onChange={onChange}
                layout={layout}
              />
            ))}
          </div>
        </div>
      )

    default:
      return null
  }
}

const CameraSettingsModal: React.FC<CameraSettingsModalProps> = ({
  isOpen,
  onClose,
  presentation = 'modal',
  slotPicker,
  leading,
  twoColumn = false,
  aside
}): JSX.Element | null => {
  const isPanel = presentation === 'panel'
  /**
   * Panel field grammar — the same row the Booth page uses: one plain title, one
   * grey hint under it, one compact control on the right. Each field is a small
   * grid so the stacked markup (label, control, hint in DOM order) lays out as a
   * row without restructuring; the standalone modal keeps its stacked classes.
   */
  const fieldClass = isPanel ? 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-6 py-3' : ''
  const fieldTitleClass = isPanel
    ? 'col-start-1 row-start-1 text-sm text-gray-800'
    : 'block text-sm font-medium mb-2 text-gray-700'
  const fieldHintClass = isPanel
    ? 'col-start-1 row-start-2 mt-0.5 text-xs text-gray-500'
    : 'text-xs text-gray-500 mt-1'
  const controlPos = 'col-start-2 row-start-1 row-span-2 justify-self-end'
  const inputBase = 'rounded-md border border-gray-300 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
  const selectClass = isPanel
    ? `${controlPos} w-64 max-w-full p-2 ${inputBase}`
    : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
  // Panel rows are flat (label left, control right); the standalone modal keeps its cards.
  // Toggle rows inside a field: the row dissolves into the field grid (`contents`),
  // its text becomes the hint under the title and the switch takes the right column.
  const rowClass = isPanel
    ? 'contents'
    : 'flex items-center justify-between p-3 border border-gray-200 rounded-md'
  const rowTextClass = isPanel ? 'col-start-1 row-start-2 min-w-0' : 'space-y-1'
  const rowLeadClass = isPanel ? 'block text-xs text-gray-500' : 'text-sm'
  const rowControlClass = isPanel
    ? `${controlPos} relative inline-flex cursor-pointer items-center`
    : 'relative inline-flex items-center cursor-pointer'
  const groupClass = isPanel ? 'col-span-2 mt-2 space-y-3' : 'space-y-3 p-3 mt-2 border border-gray-200 rounded-md'
  const { t } = useTranslation()
  const { isCompact: camDesignCompact, isPortrait: camDesignPortrait } = useDesignLayout()
  const camModalDense = camDesignCompact && camDesignPortrait
  // Display-only gate: OFF ⇒ the flash setup guide never renders (and its brand probe
  // never runs). Read here at component level; renderCameraSettings is a plain helper.
  const flashSetupGuideEnabled = useFeatureFlag(FLASH_SETUP_GUIDE_FLAG)
  // Booth default for a camera whose operator never picked a Photo Format.
  const jpegCaptureByDefault = useFeatureFlag(CAPTURE_JPEG_DEFAULT_FLAG)
  // The Focus control only means anything while prefocus is actually running. With the
  // flag off the booth never drives autofocus ahead of a shot, so offering the operator
  // "Once per session" would be offering them a lie — hide it rather than show a
  // setting that silently does nothing.
  const prefocusEnabled = useFeatureFlag(CAMERA_PREFOCUS_FLAG)
  // Circuit-breaker state for the "Reset camera engine" control below. Count only —
  // a primitive selector, so no render loop; the control merely needs to know a body
  // has been dropped to v1 and offer the operator a manual retry.
  const v2Enabled = useFeatureFlag(WASM_GPHOTO2_V2_FLAG)
  const breakerTrippedCount = useCameraEngineBreakerStore(
    (st) => Object.keys(st.records).length
  )
  const resetCameraEngine = useCameraEngineBreakerStore((st) => st.resetAll)

  /**
   * Clearing the breaker is not enough on its own: which camlib drives a body is decided
   * when it CONNECTS, so an already-connected camera keeps the engine it was built with
   * and the operator sees nothing change until the app is relaunched (field report,
   * 2026-09-16). Clear the record, then reconnect so the choice is actually re-made.
   */
  const retryFasterEngine = async (): Promise<void> => {
    resetCameraEngine()
    const cameraId = activeTab === 'primary' ? selectedCamera : selectedSecondaryCamera
    if (!cameraId) return
    const loading = cogoToast.loading(
      t('cameraSettings.cameraEngine.retrying', 'Reconnecting with the faster engine…')
    )
    try {
      await unifiedCameraUtils.disconnect(activeTab)
      const { deviceType, params } = resolveConnectArgs(cameraId)
      await unifiedCameraUtils.tryToConnect(deviceType, params, activeTab)
      cogoToast.success(t('cameraSettings.cameraEngine.retried', 'Faster engine re-armed'))
    } catch (err) {
      console.error('[CAMERA-ENGINE] retry failed:', err)
      cogoToast.error(
        t('cameraSettings.cameraEngine.retryFailed', 'Could not reconnect — select the camera again')
      )
    } finally {
      // cogoToast's loading handle exposes hide() (see SettingsModal's eject).
      if (loading?.hide) loading.hide()
    }
  }
  const {
    cameraSettings,
    setCameraSettings,
    secondaryCameraSettings,
    setSecondaryCameraSettings,
    selectedCamera,
    setSelectedCamera,
    selectedSecondaryCamera,
    cameraSlotByProject,
    setCameraSlotForProject,
    setSelectedSecondaryCamera,
    useWebcam,
    setUseWebcam,
    showCameraQualityWarnings,
    setShowCameraQualityWarnings
  } = useSettingsStore()
  const { devices, fetchDevices } = useDeviceManager()
  // Live instance state per camera slot — drives the connected/not-connected feedback
  // and the proof-of-life previews in both tabs (re-renders when a connect lands).
  const cameraStoreState = useCameraStore()

  const [activeTab, setActiveTab] = useState<CameraKey>('primary')
  const [showAdvancedCamera, setShowAdvancedCamera] = useState<Record<CameraKey, boolean>>({
    primary: false,
    secondary: false
  })

  /**
   * Jump nav for the raw config tree. The section chips sit in the pinned header,
   * but the tree they point at lives inside the Advanced disclosure — which UNMOUNTS
   * its body when closed. So with Advanced collapsed a chip found no element and the
   * tap did nothing, which reads as "the pills are broken". Open Advanced first, then
   * scroll once React has committed the body and layout has settled.
   */
  const pendingConfigJumpRef = useRef<{ cameraKey: CameraKey; sectionName: string } | null>(null)
  const revealConfigSection = (cameraKey: CameraKey, sectionName: string): void => {
    if (isPanel && !showAdvancedCamera[cameraKey]) {
      pendingConfigJumpRef.current = { cameraKey, sectionName }
      setShowAdvancedCamera((tiers) => ({ ...tiers, [cameraKey]: true }))
      return
    }
    scrollToConfigSection(cameraKey, sectionName)
  }
  useEffect(() => {
    const pending = pendingConfigJumpRef.current
    if (!pending || !showAdvancedCamera[pending.cameraKey]) return
    pendingConfigJumpRef.current = null
    // Two frames: the first commits the disclosure body, the second lets it lay out
    // so the measured header offset and target position are the real ones.
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => scrollToConfigSection(pending.cameraKey, pending.sectionName))
    })
    return (): void => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [showAdvancedCamera])

  // Preset memory. `previewSuspended` unmounts a tab's live Preview while an
  // apply runs — see the preview block below for why that is the load-bearing
  // safety step rather than a cosmetic one.
  const cameraPresetsEnabled = useFeatureFlag(CAMERA_PRESETS_FLAG)
  const [previewSuspended, setPreviewSuspended] = useState<Record<CameraKey, boolean>>({
    primary: false,
    secondary: false
  })
  /** Bumped by every manual widget edit — see handleCameraConfigChange. */
  const [configRevision, setConfigRevision] = useState<Record<CameraKey, number>>({
    primary: 0,
    secondary: 0
  })
  /** Last test photo per tab. Kept until the modal closes so settings can be compared. */
  const [testShot, setTestShot] = useState<Record<CameraKey, File | null>>({
    primary: null,
    secondary: null
  })
  const [testError, setTestError] = useState<Record<CameraKey, string | null>>({
    primary: null,
    secondary: null
  })
  const savePreset = useCameraPresetStore((s) => s.savePreset)
  const fetchPresets = useCameraPresetStore((s) => s.fetchPresets)

  /**
   * The only "save" there is. Closing this modal is the highest-signal moment in
   * the whole app — the operator has just finished tuning — so that is when the
   * camera's configuration is recorded for the booth's project. There is no name
   * field and no save button by design: the project IS the label.
   *
   * Deliberately quiet. It is best-effort background bookkeeping, so a failure
   * never blocks the close or shows the operator an error; the store's hash
   * check means an unchanged booth does not even issue the request.
   */
  const snapshotOnClose = async (): Promise<void> => {
    if (!cameraPresetsEnabled) return
    const { defaultProjectId, deviceId } = useSettingsStore.getState()
    if (!defaultProjectId) return

    for (const key of ['primary', 'secondary'] as CameraKey[]) {
      // Never snapshot a camera mid-apply: the tree is in motion and would
      // record a half-applied state as this booth's canonical configuration.
      if (isApplyInFlight(key)) continue
      const deviceType =
        key === 'primary' ? cameraStoreState.deviceType : cameraStoreState.secondaryDeviceType
      const instance =
        key === 'primary' ? cameraStoreState.camera : cameraStoreState.secondaryCamera
      if (deviceType !== 'camera' || !instance) continue

      try {
        const captured = await capturePresetFromCamera(key)
        if (!captured) continue
        await savePreset({
          projectId: defaultProjectId,
          cameraModel: captured.cameraModel,
          cameraBrand: captured.cameraBrand,
          entries: captured.entries,
          appSettings: extractAppSettings(
            key === 'primary' ? cameraSettings : secondaryCameraSettings
          ),
          appVersion: useOfflineStore.getState().appVersion ?? undefined,
          sourceDeviceId: deviceId
        })
      } catch (error) {
        console.warn(`[CAMERA-PRESET] snapshot on close failed for ${key}:`, error)
      }
    }
  }

  const closeWithSnapshot = (): void => {
    void snapshotOnClose()
    onClose()
  }

  // Refresh the preset list when the modal opens so the picker reflects what
  // other booths have saved since. Offline keeps the persisted list.
  useEffect(() => {
    if (isOpen && cameraPresetsEnabled) void fetchPresets()
  }, [isOpen, cameraPresetsEnabled, fetchPresets])

  // Pin the camera header (dropdown + live preview) only while it leaves at least
  // ~45% of the scroll viewport for the settings below it. On short viewports
  // (small monitors, high OS zoom, phones) a pinned header taller than that buries
  // the scrollable settings underneath itself — the reported bug — so it unpins
  // and scrolls away with the content instead. Height-based, not breakpoint-based,
  // because the header's own height varies with the chosen preview aspect ratio
  // and the DSLR section-chip count.
  const scrollAreaRef = useRef<HTMLDivElement | null>(null)
  const [pinHeader, setPinHeader] = useState(true)
  useEffect(() => {
    if (!isOpen) return undefined
    const scrollEl = scrollAreaRef.current
    const headerEl = document.getElementById(`camera-settings-sticky-${activeTab}`)
    if (!scrollEl || !headerEl) return undefined
    const evaluate = (): void => {
      setPinHeader(headerEl.offsetHeight <= scrollEl.clientHeight * 0.55)
    }
    evaluate()
    const ro = new ResizeObserver(evaluate)
    ro.observe(scrollEl)
    ro.observe(headerEl)
    return (): void => ro.disconnect()
  }, [isOpen, activeTab])

  const [tempSettings, setTempSettings] = useState(cameraSettings)
  const [tempSecondarySettings, setTempSecondarySettings] = useState(secondaryCameraSettings)
  const [cameraConfig, setCameraConfig] = useState<Config | null>(null)
  const [secondaryCameraConfig, setSecondaryCameraConfig] = useState<Config | null>(null)
  const [availableSettings, setAvailableSettings] = useState<AvailableSettings | null>(null)
  const [secondaryAvailableSettings, setSecondaryAvailableSettings] =
    useState<AvailableSettings | null>(null)

  const [selectedQualityPreset, setSelectedQualityPreset] = useState(() => {
    // Find matching preset or default to custom
    // Add null checks to prevent errors
    const preset = QUALITY_PRESETS.find(
      (p) =>
        cameraSettings.previewQuality &&
        p.width === cameraSettings.previewQuality.maxWidth &&
        p.height === cameraSettings.previewQuality.maxHeight &&
        p.quality === cameraSettings.previewQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedCaptureQualityPreset, setSelectedCaptureQualityPreset] = useState(() => {
    // Find matching preset for capture quality or default to custom
    const preset = QUALITY_PRESETS.find(
      (p) =>
        cameraSettings.captureQuality &&
        p.width === cameraSettings.captureQuality.maxWidth &&
        p.height === cameraSettings.captureQuality.maxHeight &&
        p.quality === cameraSettings.captureQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedSecondaryQualityPreset, setSelectedSecondaryQualityPreset] = useState(() => {
    const preset = QUALITY_PRESETS.find(
      (p) =>
        secondaryCameraSettings.previewQuality &&
        p.width === secondaryCameraSettings.previewQuality.maxWidth &&
        p.height === secondaryCameraSettings.previewQuality.maxHeight &&
        p.quality === secondaryCameraSettings.previewQuality.quality
    )
    return preset?.key || 'custom'
  })

  const [selectedSecondaryCaptureQualityPreset, setSelectedSecondaryCaptureQualityPreset] =
    useState(() => {
      const preset = QUALITY_PRESETS.find(
        (p) =>
          secondaryCameraSettings.captureQuality &&
          p.width === secondaryCameraSettings.captureQuality.maxWidth &&
          p.height === secondaryCameraSettings.captureQuality.maxHeight &&
          p.quality === secondaryCameraSettings.captureQuality.quality
      )
      return preset?.key || 'custom'
    })

  // Helper function to ensure previewQuality always exists in tempSettings
  const ensurePreviewQuality = (
    settings: Partial<typeof tempSettings>
  ): settings is typeof tempSettings & { previewQuality: NonNullable<PreviewQuality> } => {
    if (!settings.previewQuality) {
      settings.previewQuality = {
        maxWidth: 640,
        maxHeight: 480,
        quality: 0.8,
        resizeQuality: 'medium'
      }
    }
    return true
  }

  // Helper function to ensure captureQuality always exists in tempSettings
  const ensureCaptureQuality = (
    settings: Partial<typeof tempSettings>
  ): settings is typeof tempSettings & { captureQuality: NonNullable<PreviewQuality> } => {
    if (!settings.captureQuality) {
      settings.captureQuality = {
        maxWidth: 1920,
        maxHeight: 1080,
        quality: 1.0,
        resizeQuality: 'high'
      }
    }
    return true
  }

  /**
   * Which camera we are looking at, and what it can do. Deliberately SEPARATE from
   * the draft seeding below, even though both used to share one effect and one dep
   * array: `selectedCamera` has to re-run the config read, but it must NOT re-seed
   * the draft. The selection is not only changed by the operator — a run of failed
   * live-view reads clears it on its own (uniCamUtils.ts, markDeviceDisconnected
   * path), and the camera dropdown sits directly above these very controls. With
   * both jobs in one effect, a camera that blinked while the operator was choosing
   * a shutter speed silently reset every unsaved edit back to the stored value, so
   * Save then wrote the OLD value — reported as "shutter speed always goes back to
   * 1/30 when I save" (B17).
   */
  useEffect(() => {
    const fetchCameraConfig = async (cameraKey: CameraKey): Promise<void> => {
      try {
        const config = await unifiedCameraUtils.getConfig(cameraKey)
        if (config) {
          if (cameraKey === 'primary') {
            setCameraConfig(config as Config)
          } else {
            setSecondaryCameraConfig(config as Config)
          }
        }
      } catch (error) {
        console.error(`Failed to fetch ${cameraKey} camera config:`, error)
      }
    }
    if (!isOpen) return
    fetchDevices()
    if (selectedCamera) {
      fetchCameraConfig('primary')
      // Detect available settings for live view brightness feature
      detectAvailableSettings('primary').then((available) => {
        setAvailableSettings(available)
      })
    }
    if (selectedSecondaryCamera) {
      fetchCameraConfig('secondary')
      // Detect available settings for live view brightness feature
      detectAvailableSettings('secondary').then((available) => {
        setSecondaryAvailableSettings(available)
      })
    }
  }, [isOpen, selectedCamera, selectedSecondaryCamera])

  // Panel mode reads the store once: its deps include the store, so every
  // auto-commit would otherwise re-run it — a harmless temp reset, but also a
  // needless re-render per keystroke.
  const panelInitialisedRef = useRef(false)
  useEffect(() => {
    if (isPanel) {
      if (panelInitialisedRef.current) return
      panelInitialisedRef.current = true
    }
    if (isOpen) {
      // Initialize temp settings with defaults
      const initialSettings = { ...cameraSettings }
      if (!initialSettings.previewQuality) {
        initialSettings.previewQuality = {
          maxWidth: 640,
          maxHeight: 480,
          quality: 0.8,
          resizeQuality: 'medium'
        }
      }
      if (!initialSettings.captureQuality) {
        initialSettings.captureQuality = {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 1.0,
          resizeQuality: 'high'
        }
      }
      setTempSettings(initialSettings)

      const initialSecondarySettings = { ...secondaryCameraSettings }
      if (!initialSecondarySettings.previewQuality) {
        initialSecondarySettings.previewQuality = {
          maxWidth: 640,
          maxHeight: 480,
          quality: 0.8,
          resizeQuality: 'medium'
        }
      }
      if (!initialSecondarySettings.captureQuality) {
        initialSecondarySettings.captureQuality = {
          maxWidth: 1920,
          maxHeight: 1080,
          quality: 1.0,
          resizeQuality: 'high'
        }
      }
      setTempSecondarySettings(initialSecondarySettings)

      // Set quality preset
      const preset = QUALITY_PRESETS.find(
        (p) =>
          initialSettings.previewQuality &&
          p.width === initialSettings.previewQuality.maxWidth &&
          p.height === initialSettings.previewQuality.maxHeight &&
          p.quality === initialSettings.previewQuality.quality
      )
      setSelectedQualityPreset(preset?.key || 'custom')
      // Set capture quality preset
      const capturePreset = QUALITY_PRESETS.find(
        (p) =>
          initialSettings.captureQuality &&
          p.width === initialSettings.captureQuality.maxWidth &&
          p.height === initialSettings.captureQuality.maxHeight &&
          p.quality === initialSettings.captureQuality.quality
      )
      setSelectedCaptureQualityPreset(capturePreset?.key || 'custom')

      const secondaryPreset = QUALITY_PRESETS.find(
        (p) =>
          initialSecondarySettings.previewQuality &&
          p.width === initialSecondarySettings.previewQuality.maxWidth &&
          p.height === initialSecondarySettings.previewQuality.maxHeight &&
          p.quality === initialSecondarySettings.previewQuality.quality
      )
      setSelectedSecondaryQualityPreset(secondaryPreset?.key || 'custom')
      const secondaryCapturePreset = QUALITY_PRESETS.find(
        (p) =>
          initialSecondarySettings.captureQuality &&
          p.width === initialSecondarySettings.captureQuality.maxWidth &&
          p.height === initialSecondarySettings.captureQuality.maxHeight &&
          p.quality === initialSecondarySettings.captureQuality.quality
      )
      setSelectedSecondaryCaptureQualityPreset(secondaryCapturePreset?.key || 'custom')
    }
    // Seeding only. The selected camera is NOT a dependency on purpose — see the
    // effect above.
  }, [isOpen, cameraSettings, secondaryCameraSettings])

  /**
   * Panel mode commits on change, like every other control in the pane it now
   * lives in. Debounced so a slider drag is one write, not thirty. Safe from a
   * loop: the reset effect above runs once in panel mode, so a store write never
   * feeds back into temp state.
   */
  const committedSnapshot = JSON.stringify([tempSettings, tempSecondarySettings])
  const skipFirstCommitRef = useRef(true)
  const draftRef = useRef({ primary: tempSettings, secondary: tempSecondarySettings })
  draftRef.current = { primary: tempSettings, secondary: tempSecondarySettings }
  /**
   * Write the draft to the store only where it would change a value. The store
   * setters merge into a NEW object on every call, and the live preview restarts
   * its frame loop whenever its settings object changes identity — so a no-op
   * commit (a keystroke that lands on the same value, a tab switch) showed up as
   * a preview glitch on the booth.
   */
  const commitDraft = (draft: typeof draftRef.current): void => {
    const store = useSettingsStore.getState()
    if (
      JSON.stringify({ ...store.cameraSettings, ...draft.primary }) !==
      JSON.stringify(store.cameraSettings)
    ) {
      setCameraSettings(draft.primary)
    }
    if (
      JSON.stringify({ ...store.secondaryCameraSettings, ...draft.secondary }) !==
      JSON.stringify(store.secondaryCameraSettings)
    ) {
      setSecondaryCameraSettings(draft.secondary)
    }
  }
  useEffect(() => {
    if (!isPanel) return
    if (skipFirstCommitRef.current) {
      skipFirstCommitRef.current = false
      return
    }
    const id = setTimeout(() => commitDraft(draftRef.current), 250)
    return (): void => clearTimeout(id)
  }, [isPanel, committedSnapshot])
  // The page unmounts when the rail switches; a change made inside the debounce
  // window must still land.
  useEffect(() => {
    if (!isPanel) return
    return (): void => commitDraft(draftRef.current)
  }, [isPanel])

  const handleCameraConfigChange = async (
    cameraKey: CameraKey,
    name: string,
    value: ConfigValue
  ): Promise<void> => {
    try {
      await unifiedCameraUtils.setConfigValue(cameraKey, name, value)
      // Every hand-edit bumps this tab's revision. The preset panel compares it
      // against the revision it recorded at apply time, which is what lets it
      // say "Modified from <booth>" instead of continuing to claim the camera
      // still matches the preset the operator applied.
      setConfigRevision((prev) => ({ ...prev, [cameraKey]: (prev[cameraKey] || 0) + 1 }))
      // Refresh the config after setting a value
      const newConfig = await unifiedCameraUtils.getConfig(cameraKey)
      if (newConfig) {
        if (cameraKey === 'primary') {
          setCameraConfig(newConfig as Config)
        } else {
          setSecondaryCameraConfig(newConfig as Config)
        }
      }
    } catch (error) {
      console.error(`Failed to update ${cameraKey} camera config:`, error)
    }
  }

  const handleQualityPresetChange = (cameraKey: CameraKey, presetKey: string): void => {
    if (cameraKey === 'primary') {
      setSelectedQualityPreset(presetKey)
    } else {
      setSelectedSecondaryQualityPreset(presetKey)
    }

    if (presetKey === 'custom') return

    const preset = QUALITY_PRESETS.find((p) => p.key === presetKey)
    if (preset && preset.width && preset.height && preset.quality && preset.resizeQuality) {
      const newQuality = {
        previewQuality: {
          maxWidth: preset.width,
          maxHeight: preset.height,
          quality: preset.quality,
          resizeQuality: preset.resizeQuality as ResizeQuality
        }
      }
      if (cameraKey === 'primary') {
        setTempSettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      } else {
        setTempSecondarySettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      }
    }
  }

  const handleCaptureQualityPresetChange = (cameraKey: CameraKey, presetKey: string): void => {
    if (cameraKey === 'primary') {
      setSelectedCaptureQualityPreset(presetKey)
    } else {
      setSelectedSecondaryCaptureQualityPreset(presetKey)
    }

    if (presetKey === 'custom') return

    const preset = QUALITY_PRESETS.find((p) => p.key === presetKey)
    if (preset && preset.width && preset.height && preset.quality && preset.resizeQuality) {
      const newQuality = {
        captureQuality: {
          maxWidth: preset.width,
          maxHeight: preset.height,
          quality: preset.quality,
          resizeQuality: preset.resizeQuality as ResizeQuality
        }
      }
      if (cameraKey === 'primary') {
        setTempSettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      } else {
        setTempSecondarySettings((prev) => ({
          ...prev,
          ...newQuality
        }))
      }
    }
  }

  const handleCameraChange = async (cameraKey: CameraKey, newCameraId: string): Promise<void> => {
    const isPrimary = cameraKey === 'primary'
    const setSelected = isPrimary ? setSelectedCamera : setSelectedSecondaryCamera
    setSelected(newCameraId)

    try {
      if (!newCameraId) {
        await unifiedCameraUtils.disconnect(cameraKey)
        if (isPrimary) {
          setCameraConfig(null)
        } else {
          setSecondaryCameraConfig(null)
        }
        return
      }

      // Handles every selection format: webcam deviceId, legacy bare productId, and the
      // serial-qualified `usb:vid:pid:serial` form that distinguishes same-series twins.
      const { deviceType, params } = resolveConnectArgs(newCameraId)

      await unifiedCameraUtils.tryToConnect(deviceType, params, cameraKey)
      const newConfig = await unifiedCameraUtils.getConfig(cameraKey)
      if (isPrimary) {
        setCameraConfig(newConfig as Config)
      } else {
        setSecondaryCameraConfig(newConfig as Config)
      }
    } catch (error) {
      console.error(`Failed to connect or configure ${cameraKey} camera:`, error)
      cogoToast.error(
        `Failed to connect to ${cameraKey} camera: ${error instanceof Error ? error.message : String(error)}`
      )
    }
  }

  // Multi-project hub: "use this camera for the active project" binding. A hub
  // never captures, so it is never bindable — same rule the printer modal uses.
  const activeProjectId = useConfigStore((s) => s.config?._id)
  // Binding a camera to "the active project" only means something inside a hub.
  const inHub = useHubStore((s) => Boolean(s.hubId))
  const activeProjectTitle = useConfigStore((s) => s.config?.title)
  const activeProjectIsHub = useConfigStore((s) =>
    Boolean(s.config?.pages?.modeSelect?.settings?.enabled)
  )

  if (!isOpen) return null

  const renderCameraSettings = (
    cameraKey: CameraKey,
    part: 'controls' | 'preview' | 'picker' = 'controls'
  ): JSX.Element => {
    const isPrimary = cameraKey === 'primary'
    const settings = isPrimary ? tempSettings : tempSecondarySettings
    const setSettings = isPrimary ? setTempSettings : setTempSecondarySettings
    const qualityPreset = isPrimary ? selectedQualityPreset : selectedSecondaryQualityPreset
    const captureQualityPreset = isPrimary
      ? selectedCaptureQualityPreset
      : selectedSecondaryCaptureQualityPreset
    const captureFormat = normaliseCaptureFormat(
      settings.captureFormat,
      captureFormatDefault(jpegCaptureByDefault)
    )
    const config = isPrimary ? cameraConfig : secondaryCameraConfig
    const selectedCam = isPrimary ? selectedCamera : selectedSecondaryCamera

    // Live connection state for THIS tab's camera — the previous UI gave no feedback
    // at all (an operator could select a device, have the connect fail or a feature
    // flag suppress dual mode, and never know). Instance-in-store = stream is open.
    const liveInstance = isPrimary
      ? cameraStoreState.webcam || cameraStoreState.camera
      : cameraStoreState.secondaryWebcam || cameraStoreState.secondaryCamera
    const isConnected = Boolean(liveInstance)

    // Shutter-capture risk scan: with shutter mode ON, every shot is a full-size file
    // transfer over the single USB PTP channel BEFORE the next countdown can start —
    // Large Fine JPEG ≈ many seconds of frozen booth per shot; RAW worse. Surface the
    // risky on-camera values right where the operator enables the mode.
    const shutterRisks: CaptureRisk[] = settings.useShutterCapture
      ? assessShutterCaptureRisks(config)
      : []
    // Flash setup guide brand hint. Only computed when the guide will actually render
    // (operator has declared flash use via the brighten-liveview toggle) — the silent-LV
    // menu settings it lists are camera-menu-only and invisible over PTP, so the guide is
    // unconditional education for flash users, never shown to non-flash booths.
    const showFlashGuide =
      flashSetupGuideEnabled &&
      settings.useShutterCapture &&
      settings.autoBrightenLiveViewForFlash
    const flashGuideBrand: CameraBrand = showFlashGuide
      ? detectCameraBrandFromConfig(config)
      : 'unknown'
    // libgphoto2 roots are a 'window' whose children are the sections — that's what
    // the jump nav indexes. Single-section (or webcam: no config) renders no nav.
    const configSections = config?.children ? Object.entries(config.children) : []

    // Preview box sizing: every ratio is height-budgeted against the modal itself
    // (see previewBoxStyleForRatio). The DISPLAY ratio accounts for preview rotation:
    // at 90°/270° the on-screen feed's aspect is the inverse of the capture ratio,
    // and a box shaped like the un-rotated ratio would crop the covered feed.
    const previewRatioNum =
      typeof settings.captureRatio === 'number' && settings.captureRatio > 0
        ? settings.captureRatio
        : 16 / 9
    const previewRotated = settings.previewRotation === 90 || settings.previewRotation === 270
    const previewBoxStyle = previewBoxStyleForRatio(
      previewRotated ? 1 / previewRatioNum : previewRatioNum
    )

    /** Live preview with the shutter riding on it. The panel shows it beside the controls. */
    const cameraPreviewBlock = (
      <>
          {isConnected && previewSuspended[cameraKey] ? (
            /* A preset apply is running. The Preview is UNMOUNTED, not hidden:
               it is the frame pull loop that has to stop — parkCamera refuses
               outright while a live preview is on screen, and every config write
               would otherwise contend with a liveview read on the one PTP
               channel. */
            <div
              className="mt-2 mx-auto flex items-center justify-center rounded-md bg-gray-100 px-4 text-center text-xs text-gray-500"
              style={previewBoxStyleForRatio(16 / 9)}
            >
              {t('cameraPresets.previewPaused', 'Preview paused while camera settings are applied')}
            </div>
          ) : isConnected ? (
            <div className="mt-2 flex justify-center">
              <div
                className="relative bg-black rounded-md overflow-hidden"
                style={previewBoxStyle}
              >
                {/* Keyed per slot in the panel: switching tabs remounts the preview instead of
                    re-running one instance's frame loop with another camera's key. */}
                <Preview
                  key={isPanel ? cameraKey : undefined}
                  cameraKey={cameraKey}
                  fillContainer
                  settingsOverride={settings}
                />
                {/* Shutter and last shot ride ON the preview, phone-camera style.
                    They were a 48px button plus two lines of prose underneath,
                    which spent scarce vertical space explaining what the
                    control's own shape says. */}
                <CameraTestShutter
                  cameraKey={cameraKey}
                  disabled={previewSuspended[cameraKey]}
                  onShot={(file) => {
                    setTestShot((prev) => ({ ...prev, [cameraKey]: file }))
                    setTestError((prev) => ({ ...prev, [cameraKey]: null }))
                  }}
                  onError={(message) =>
                    setTestError((prev) => ({ ...prev, [cameraKey]: message }))
                  }
                />
                <CameraTestResult
                  file={testShot[cameraKey]}
                  error={null}
                  onClear={() => setTestShot((prev) => ({ ...prev, [cameraKey]: null }))}
                />
              </div>
            </div>
          ) : (
            <div
              className="mt-2 mx-auto bg-gray-100 rounded-md flex items-center justify-center text-gray-400 text-xs text-center px-4"
              style={previewBoxStyleForRatio(16 / 9)}
            >
              {selectedCam
                ? t('settings.camera.previewWhenConnected', 'Live preview appears once the camera connects')
                : t('settings.camera.selectToPreview', 'Select a camera to see a live preview')}
            </div>
          )}

          {testError[cameraKey] && (
            <p className="mt-1 text-center text-[11px] text-red-600">{testError[cameraKey]}</p>
          )}
      </>
    )
    /** The slot's picker, its connection line and the DSLR section chips — the header
        of the standalone modal, and the sticky strip beside the tabs in the panel. */
    const cameraPickerRow = (
          <div className="flex gap-2">
            <select
              value={selectedCam || ''}
              onChange={(e) => handleCameraChange(cameraKey, e.target.value)}
              className={selectClass}
              title={t('settings.camera.title')}
            >
              <option value="">{t('settings.camera.selectCamera')}</option>
              {disambiguateWebcamLabels(devices?.cameras.webcams || []).map((webcam) => (
                <option key={webcam.deviceId} value={webcam.deviceId}>
                  {webcam.label || t('settings.camera.unknownWebcam')}
                </option>
              ))}
              {disambiguateUsbCameraLabels(devices?.cameras.usbCameras || []).map(({ camera, idx, suffix, ordinal }) => (
                <option
                  key={`${camera.vendorId}-${camera.productId}-${camera.serialNumber ?? idx}`}
                  value={makeUsbSelectionValueForDevice(camera, ordinal)}
                >
                  {camera.brand}
                  {usbCameraSerialSuffix(camera)}
                  {suffix} (
                  {t(cameraDriverStatusKey(camera.driverState, camera.hasDriver))})
                </option>
              ))}
            </select>
          </div>
    )
    const cameraStatusLine = (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${
                isConnected ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />
            <span className={isConnected ? 'text-green-700' : 'text-gray-500'}>
              {isConnected
                ? t('settings.camera.connected', 'Connected — live preview below')
                : selectedCam
                  ? t('settings.camera.notConnected', 'Not connected — select the camera again or check the cable')
                  : t('settings.camera.noneSelected', 'No camera selected')}
            </span>
          </div>
    )
    const configJumpChips = (
      <>
          {/* Section jump nav: DSLRs expose dozens of config widgets across many
              sections — pinned chips let the operator jump straight to one instead
              of scrolling the whole list. Rendered only when a config is loaded. */}
          {configSections.length > 1 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {configSections.map(([sectionName, section]) => (
                <button
                  type="button"
                  key={sectionName}
                  onClick={() => revealConfigSection(cameraKey, sectionName)}
                  className="rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-[11px] text-gray-600 hover:border-blue-400 hover:text-blue-600"
                >
                  {section.label || sectionName}
                </button>
              ))}
            </div>
          )}
      </>
    )
    if (part === 'preview') return cameraPreviewBlock
    if (part === 'picker') {
      return (
        <>
          {isPrimary && slotPicker ? slotPicker : cameraPickerRow}
          {cameraStatusLine}
          {configJumpChips}
        </>
      )
    }

    return (
      <div className="space-y-6">
        {/* Sticky header: the camera dropdown + a live preview stay pinned to the top while the
            operator scrolls the app/DSLR settings below, so they never lose sight of which camera
            they're tuning or how it looks. The preview reacts to UNSAVED edits via settingsOverride.
            When the header would eat most of the scroll viewport (pinHeader false), it unpins and
            scrolls away like normal content — nothing may ever hide the settings permanently. */}
        {!isPanel && (
        <div
          id={`camera-settings-sticky-${cameraKey}`}
          className={`pt-1 pb-3 bg-white ${
            pinHeader ? 'sticky top-0 z-20 border-b border-gray-200 shadow-sm rounded-b-lg' : ''
          }`}
        >
          <label className="block text-sm font-medium mb-2 text-gray-700">
            {isPrimary
              ? t('settings.camera.primaryTitle', 'Primary Camera')
              : t('settings.camera.secondaryTitle', 'Secondary Camera')}
          </label>
          {cameraPickerRow}
          {cameraStatusLine}
          {cameraPreviewBlock}
          {configJumpChips}
        </div>
        )}
        <div>
          {!isPanel && (
          <h3 className="text-xl font-semibold mb-4">
            {t('cameraSettings.appSettings', 'App Settings')}
          </h3>
          )}
          {!isPanel && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  {t('cameraSettings.appSettingsHint.title', 'App Settings Guide')}
                </h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    {t(
                      'cameraSettings.appSettingsHint.description',
                      'These settings control how the app interacts with your camera. Adjust preview quality and capture ratio to match your needs. Lower quality settings can improve performance.'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
          )}
          <div className={isPanel ? 'divide-y divide-gray-100' : 'space-y-4'}>
            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.captureRatio.title', 'Capture Ratio')}
              </label>
              <select
                title={t('cameraSettings.captureRatio.title', 'Capture Ratio')}
                value={settings.captureRatio || ''}
                onChange={(e) => {
                  const nextValue = e.target.value
                  let nextRatio: number | 'auto' | undefined

                  if (!nextValue || nextValue === 'auto') {
                    nextRatio = 'auto'
                  } else {
                    const parsedRatio = parseFloat(nextValue)
                    nextRatio = Number.isFinite(parsedRatio) ? parsedRatio : 'auto'
                  }

                  setSettings((prev) => ({
                    ...prev,
                    captureRatio: nextRatio
                  }))
                }}
                className={selectClass}
              >
                {RATIO_OPTIONS.map((option) => (
                  <option key={option.key} value={option.value || ''}>
                    {t(`cameraSettings.captureRatio.options.${option.key}`, option.label)}
                  </option>
                ))}
              </select>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.mirror.title', 'Mirror')}
              </label>
              <div className={rowClass}>
                <div className={rowTextClass}>
                  {!isPanel && (
                  <span className={rowLeadClass}>
                    {t('cameraSettings.mirror.enable', 'Mirror camera preview and captured image')}
                  </span>
                  )}
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.mirror.description',
                      'Applies horizontal flip to both the live preview and saved photos. Can be combined with any rotation.'
                    )}
                  </p>
                </div>
                <label className={rowControlClass}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.mirror}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        mirror: e.target.checked
                      }))
                    }
                    aria-label={t('cameraSettings.mirror.enable', 'Toggle mirror')}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            {/* Quality, rotation, filter-on-preview, shutter, webcam mode, presets and
                the raw camera config tree: what a booth touches once, if ever. */}
            <SettingsDisclosure
              enabled={isPanel}
              title={t('settings.tiers.advancedCamera', 'Advanced')}
              hint={t('settings.tiers.advancedCameraHint', 'Quality, rotation, presets and camera configuration')}
              open={showAdvancedCamera[cameraKey]}
              onToggle={() =>
                setShowAdvancedCamera((tiers) => ({ ...tiers, [cameraKey]: !tiers[cameraKey] }))
              }
            >
        {cameraPresetsEnabled && (
          <CameraPresetsPanel
            cameraKey={cameraKey}
            config={config as CameraPresetConfigNode | null}
            isConnected={isConnected}
            configRevision={configRevision[cameraKey] || 0}
            onSuspendPreviewChange={(suspended) =>
              setPreviewSuspended((prev) => ({ ...prev, [cameraKey]: suspended }))
            }
            onConfigChangedExternally={() => {
              // An apply or undo rewrote the camera; re-read so the widget tree
              // below the panel shows the new values instead of stale ones.
              void unifiedCameraUtils.getConfig(cameraKey).then((next) => {
                if (!next) return
                if (cameraKey === 'primary') setCameraConfig(next as Config)
                else setSecondaryCameraConfig(next as Config)
              })
            }}
          />
        )}
            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.previewQuality.title', 'Preview Quality')}
              </label>
              <select
                value={qualityPreset}
                onChange={(e) => handleQualityPresetChange(cameraKey, e.target.value)}
                className={selectClass}
                title={t('cameraSettings.previewQuality.title', 'Preview Quality')}
                aria-label={t('cameraSettings.previewQuality.title', 'Preview Quality')}
              >
                {QUALITY_PRESETS.filter((p) => !p.captureOnly).map((preset) => (
                  <option key={preset.key} value={preset.key}>
                    {t(`cameraSettings.qualityPresets.${preset.key}`)}
                  </option>
                ))}
                <option value="custom">
                  {t('cameraSettings.previewQuality.options.custom', 'Custom')}
                </option>
              </select>

              {qualityPreset === 'custom' && (
                <div className={groupClass}>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                    </label>
                    <input
                      type="number"
                      min="320"
                      max="1920"
                      step="80"
                      value={settings.previewQuality?.maxWidth || 640}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1920) value = 1920
                        setSettings((prev) => {
                          if (ensurePreviewQuality(prev)) {
                            const newPreviewQuality: PreviewQuality = {
                              ...prev.previewQuality,
                              maxWidth: value
                            }
                            return {
                              ...prev,
                              previewQuality: newPreviewQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                      aria-label={t('cameraSettings.previewQuality.maxWidth', 'Max Width')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                    </label>
                    <input
                      type="number"
                      min="240"
                      max="1080"
                      step="60"
                      value={settings.previewQuality?.maxHeight || 480}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > 1080) value = 1080
                        setSettings((prev) => {
                          if (ensurePreviewQuality(prev)) {
                            const newPreviewQuality: PreviewQuality = {
                              ...prev.previewQuality,
                              maxHeight: value
                            }
                            return {
                              ...prev,
                              previewQuality: newPreviewQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                      aria-label={t('cameraSettings.previewQuality.maxHeight', 'Max Height')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0.4"
                        max="1"
                        step="0.1"
                        value={settings.previewQuality?.quality || 0.8}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          setSettings((prev) => {
                            if (ensurePreviewQuality(prev)) {
                              const newPreviewQuality: PreviewQuality = {
                                ...prev.previewQuality,
                                quality: value
                              }
                              return {
                                ...prev,
                                previewQuality: newPreviewQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                        aria-label={t('cameraSettings.previewQuality.quality', 'JPEG Quality')}
                      />
                      <span className="text-sm font-medium w-16">
                        {Math.round((settings.previewQuality?.quality || 0.8) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.previewQuality.resizeQuality', 'Resize Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <select
                        value={settings.previewQuality?.resizeQuality || 'medium'}
                        onChange={(e) => {
                          const value = e.target.value as ResizeQuality
                          setSettings((prev) => {
                            if (ensurePreviewQuality(prev)) {
                              const newPreviewQuality: PreviewQuality = {
                                ...prev.previewQuality,
                                resizeQuality: value
                              }
                              return {
                                ...prev,
                                previewQuality: newPreviewQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.previewQuality.resizeQuality', 'Resize Quality')}
                        aria-label={t(
                          'cameraSettings.previewQuality.resizeQuality',
                          'Resize Quality'
                        )}
                      >
                        {RESIZE_QUALITY_OPTIONS.map((resizeQuality) => (
                          <option key={resizeQuality} value={resizeQuality}>
                            {t(`cameraSettings.previewQuality.options.${resizeQuality}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <p className={fieldHintClass}>
                {t(
                  'cameraSettings.previewQuality.description',
                  'Lower quality improves performance and reduces lag'
                )}
              </p>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
              </label>
              <select
                value={captureQualityPreset}
                onChange={(e) => handleCaptureQualityPresetChange(cameraKey, e.target.value)}
                className={selectClass}
                title={t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
                aria-label={t('cameraSettings.captureQuality.title', 'Capture Image Quality')}
              >
                {/* Every tier is selectable on every plan — the cost is communicated
                    through the per-option estimate and the impact badge below, not by
                    locking the option. */}
                {QUALITY_PRESETS.map((preset) => {
                  const presetFootprint = estimateCaptureFootprint(
                    preset.width,
                    preset.height,
                    preset.quality,
                    captureFormat
                  )
                  return (
                    <option key={preset.key} value={preset.key}>
                      {`${t(`cameraSettings.qualityPresets.${preset.key}`)} · ${t(
                        'cameraSettings.captureImpact.optionEstimate',
                        { size: formatMegabytes(presetFootprint.megabytes) }
                      )}`}
                    </option>
                  )
                })}
                <option value="custom">
                  {t('cameraSettings.previewQuality.options.custom', 'Custom')}
                </option>
              </select>

              <CaptureImpactBadge
                width={settings.captureQuality?.maxWidth || 1920}
                height={settings.captureQuality?.maxHeight || 1080}
                quality={settings.captureQuality?.quality || 1}
                format={captureFormat}
                tooltipId={`capture-impact-tip-${cameraKey}`}
              />

              {captureQualityPreset === 'custom' && (
                <div className={groupClass}>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                    </label>
                    <input
                      type="number"
                      min="320"
                      max={MAX_CAPTURE_WIDTH}
                      step="80"
                      value={settings.captureQuality?.maxWidth || 1920}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > MAX_CAPTURE_WIDTH) value = MAX_CAPTURE_WIDTH
                        setSettings((prev) => {
                          if (ensureCaptureQuality(prev)) {
                            const newCaptureQuality: PreviewQuality = {
                              ...prev.captureQuality,
                              maxWidth: value
                            }
                            return {
                              ...prev,
                              captureQuality: newCaptureQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                      aria-label={t('cameraSettings.captureQuality.maxWidth', 'Max Width')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                    </label>
                    <input
                      type="number"
                      min="240"
                      max={MAX_CAPTURE_HEIGHT}
                      step="60"
                      value={settings.captureQuality?.maxHeight || 1080}
                      onChange={(e) => {
                        let value = parseInt(e.target.value)
                        if (value > MAX_CAPTURE_HEIGHT) value = MAX_CAPTURE_HEIGHT
                        setSettings((prev) => {
                          if (ensureCaptureQuality(prev)) {
                            const newCaptureQuality: PreviewQuality = {
                              ...prev.captureQuality,
                              maxHeight: value
                            }
                            return {
                              ...prev,
                              captureQuality: newCaptureQuality
                            }
                          }
                          return prev
                        })
                      }}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      title={t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                      aria-label={t('cameraSettings.captureQuality.maxHeight', 'Max Height')}
                    />
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        min="0.4"
                        max="1"
                        step="0.1"
                        value={settings.captureQuality?.quality || 1.0}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value)
                          setSettings((prev) => {
                            if (ensureCaptureQuality(prev)) {
                              const newCaptureQuality: PreviewQuality = {
                                ...prev.captureQuality,
                                quality: value
                              }
                              return {
                                ...prev,
                                captureQuality: newCaptureQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                        aria-label={t('cameraSettings.captureQuality.quality', 'JPEG Quality')}
                      />
                      <span className="text-sm font-medium w-16">
                        {Math.round((settings.captureQuality?.quality || 1.0) * 100)}%
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs text-gray-600 mb-1">
                      {t('cameraSettings.captureQuality.resizeQuality', 'Resize Quality')}
                    </label>
                    <div className="flex items-center gap-4">
                      <select
                        value={settings.captureQuality?.resizeQuality || 'high'}
                        onChange={(e) => {
                          const value = e.target.value as ResizeQuality
                          setSettings((prev) => {
                            if (ensureCaptureQuality(prev)) {
                              const newCaptureQuality: PreviewQuality = {
                                ...prev.captureQuality,
                                resizeQuality: value
                              }
                              return {
                                ...prev,
                                captureQuality: newCaptureQuality
                              }
                            }
                            return prev
                          })
                        }}
                        className="flex-1"
                        title={t('cameraSettings.captureQuality.resizeQuality', 'Resize Quality')}
                        aria-label={t(
                          'cameraSettings.captureQuality.resizeQuality',
                          'Resize Quality'
                        )}
                      >
                        {RESIZE_QUALITY_OPTIONS.map((resizeQuality) => (
                          <option key={resizeQuality} value={resizeQuality}>
                            {t(`cameraSettings.previewQuality.options.${resizeQuality}`)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              )}

              <p className={fieldHintClass}>
                {t(
                  'cameraSettings.captureQuality.description',
                  'Adjust resolution and quality for captured photos. Higher quality results in larger file sizes.'
                )}
              </p>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.previewRotation.title', 'Preview Rotation')}
              </label>
              <p className={fieldHintClass}>
                {t(
                  'cameraSettings.previewRotation.description',
                  'Rotate the live camera feed on screen. Does not affect the saved photo.'
                )}
              </p>
              <div className={rowClass}>
                {!isPanel && (
                <span className={rowLeadClass}>
                  {t('cameraSettings.previewRotation.label', 'Live preview rotation')}
                </span>
                )}
                <select
                  value={settings.previewRotation}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      previewRotation: parseInt(e.target.value)
                    }))
                  }
                  className={isPanel ? `${controlPos} w-28 p-2 ${inputBase}` : 'p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}
                  title={t('cameraSettings.previewRotation.title', 'Preview Rotation')}
                  aria-label={t('cameraSettings.previewRotation.title', 'Preview Rotation')}
                >
                  <option value={0}>0°</option>
                  <option value={90}>90°</option>
                  <option value={180}>180°</option>
                  <option value={270}>270°</option>
                </select>
              </div>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.captureRotation.title', 'Capture Rotation')}
              </label>
              <p className={fieldHintClass}>
                {t(
                  'cameraSettings.captureRotation.description',
                  'Rotate the saved photo pixels. Use this when your camera already applies its own rotation to shutter captures.'
                )}
              </p>
              <div className={rowClass}>
                {!isPanel && (
                <span className={rowLeadClass}>
                  {t('cameraSettings.captureRotation.label', 'Saved image rotation')}
                </span>
                )}
                <select
                  value={settings.captureRotation}
                  onChange={(e) =>
                    setSettings((prev) => ({
                      ...prev,
                      captureRotation: parseInt(e.target.value)
                    }))
                  }
                  className={isPanel ? `${controlPos} w-28 p-2 ${inputBase}` : 'p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500'}
                  title={t('cameraSettings.captureRotation.title', 'Capture Rotation')}
                  aria-label={t('cameraSettings.captureRotation.title', 'Capture Rotation')}
                >
                  <option value={0}>0°</option>
                  <option value={90}>90°</option>
                  <option value={180}>180°</option>
                  <option value={270}>270°</option>
                </select>
              </div>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.applyFilterOnPreview.title', 'Apply Filter on Preview')}
              </label>
              <div className={rowClass}>
                <div className={rowTextClass}>
                  {!isPanel && (
                  <span className={rowLeadClass}>
                    {t(
                      'cameraSettings.applyFilterOnPreview.enable',
                      'Apply filter effects on live preview'
                    )}
                  </span>
                  )}
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.applyFilterOnPreview.description',
                      'Disabling this will improve preview performance'
                    )}
                  </p>
                </div>
                <label className={rowControlClass}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.applyFilterOnPreview}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        applyFilterOnPreview: e.target.checked
                      }))
                    }
                    aria-label={t(
                      'cameraSettings.applyFilterOnPreview.enable',
                      'Toggle filter on preview'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.useShutterCapture.title', 'Use shutter capture')}
              </label>
              <div className={rowClass}>
                <div className={rowTextClass}>
                  <span className={rowLeadClass}>
                    {t(
                      'cameraSettings.useShutterCapture.description',
                      'Use the camera physical shutter for capturing photos. When disabled, the app will capture the current preview frame instead. Use shutter mode for better quality photos, or preview capture for faster response.'
                    )}
                  </span>
                </div>
                <label className={rowControlClass}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={settings.useShutterCapture}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        useShutterCapture: e.target.checked
                      }))
                    }
                    aria-label={t(
                      'cameraSettings.useShutterCapture.enable',
                      'Toggle shutter capture'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            {/* Only meaningful on the physical-shutter path: the preview-grab path never
                fires a shutter, so there is no autofocus wait to schedule around. And
                only while prefocus is on, so an unflagged booth's Camera Settings is
                byte-for-byte what it was. */}
            {prefocusEnabled && settings.useShutterCapture && (
              <div className={fieldClass}>
                <label className={fieldTitleClass} htmlFor="camera-focus-mode">
                  {t('cameraSettings.focusMode.title', 'Focus')}
                </label>
                <div className={isPanel ? 'contents' : 'p-3 border border-gray-200 rounded-md space-y-2'}>
                  <span className={isPanel ? fieldHintClass : 'text-sm block'}>
                    {t(
                      'cameraSettings.focusMode.description',
                      'When the booth focuses before taking a photo. Focusing happens during the countdown, so the shutter is not waiting on it.'
                    )}
                  </span>
                  <select
                    id="camera-focus-mode"
                    value={normaliseFocusMode(settings.focusMode)}
                    onChange={(e) =>
                      setSettings((prev) => ({
                        ...prev,
                        focusMode: normaliseFocusMode(e.target.value)
                      }))
                    }
                    className={selectClass}
                    title={t('cameraSettings.focusMode.title', 'Focus')}
                    aria-label={t('cameraSettings.focusMode.title', 'Focus')}
                  >
                    <option value="every-photo">
                      {t('cameraSettings.focusMode.everyPhoto', 'Every photo (default)')}
                    </option>
                    <option value="once-per-session">
                      {t('cameraSettings.focusMode.oncePerSession', 'Once per session')}
                    </option>
                    <option value="fixed">
                      {t('cameraSettings.focusMode.fixed', 'Fixed — never focus')}
                    </option>
                  </select>
                  <span className={isPanel ? 'col-span-2 mt-1 block text-xs text-gray-500' : 'text-xs text-gray-500 block'}>
                    {t(
                      `cameraSettings.focusMode.hint.${normaliseFocusMode(settings.focusMode)}`,
                      FOCUS_MODE_FALLBACK_HINTS[normaliseFocusMode(settings.focusMode)]
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Its own row, in the same shape as every other toggle here: title left,
                hint beneath it, switch right-aligned. Shown on the same terms as the
                Focus control above, since it only decides what happens to that focus. */}
            {prefocusEnabled && settings.useShutterCapture && (
              <div className={fieldClass}>
                <label className={fieldTitleClass} htmlFor="camera-wait-for-focus">
                  {t('cameraSettings.waitForFocus.title', 'Wait for focus before taking the photo')}
                </label>
                <div className={rowClass}>
                  <div className={rowTextClass}>
                    <span className={rowLeadClass}>
                      {t(
                        'cameraSettings.waitForFocus.description',
                        'Off by default: the photo is taken the moment the countdown ends, even if focus is still settling. Turn this on to hold the shutter until focus is done — sharper photos, but the guest waits a moment after the countdown.'
                      )}
                    </span>
                  </div>
                  <label className={rowControlClass}>
                    <input
                      id="camera-wait-for-focus"
                      type="checkbox"
                      className="sr-only peer"
                      checked={settings.waitForFocusBeforeCapture ?? WAIT_FOR_FOCUS_DEFAULT}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          waitForFocusBeforeCapture: e.target.checked
                        }))
                      }
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                  </label>
                </div>
              </div>
            )}

            {v2Enabled && breakerTrippedCount > 0 && (
              <div className={fieldClass}>
                <label className={fieldTitleClass}>
                  {t('cameraSettings.cameraEngine.title', 'Camera engine')}
                </label>
                <div className={isPanel ? 'contents' : 'p-3 border border-gray-200 rounded-md space-y-2'}>
                  <span className={isPanel ? fieldHintClass : 'text-sm block'}>
                    {t(
                      'cameraSettings.cameraEngine.trippedHint',
                      'A camera dropped to the standard engine after it failed the faster one. It will retry automatically on the next update.'
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => void retryFasterEngine()}
                    className={
                      isPanel
                        ? 'justify-self-start px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50'
                        : 'px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50'
                    }
                  >
                    {t('cameraSettings.cameraEngine.reset', 'Retry faster engine now')}
                  </button>
                </div>
              </div>
            )}

            {settings.useShutterCapture && shutterRisks.length > 0 && (
              <div className="border-l-4 border-red-400 bg-red-50 p-4">
                <h4 className="text-sm font-semibold text-red-800">
                  {t(
                    'cameraSettings.shutterRisk.title',
                    'Shutter capture will be SLOW with the current camera settings'
                  )}
                </h4>
                <p className="mt-1 text-xs text-red-700">
                  {t(
                    'cameraSettings.shutterRisk.description',
                    'Every shutter shot transfers the full-size file over USB before the next countdown can start — large files freeze the booth for many seconds between photos. Small Fine JPEG is the recommended setting for photobooths.'
                  )}
                </p>
                <ul className="mt-2 space-y-1.5">
                  {shutterRisks.map((risk) => (
                    <li key={`${risk.settingName}-${risk.value}`} className="text-xs text-red-700">
                      <span className="font-medium">{risk.settingLabel}:</span>{' '}
                      <span className="font-mono">&quot;{risk.value}&quot;</span>
                      {' — '}
                      {risk.reason === 'raw' &&
                        t(
                          'cameraSettings.shutterRisk.raw',
                          'RAW files are 25–45 MB per shot and cannot be processed by the booth pipeline. Switch to Small Fine JPEG.'
                        )}
                      {risk.reason === 'large' &&
                        t(
                          'cameraSettings.shutterRisk.large',
                          'Large files take up to ~10 seconds to transfer after every shot. Switch to Small Fine JPEG.'
                        )}
                      {risk.reason === 'medium' &&
                        t(
                          'cameraSettings.shutterRisk.medium',
                          'Medium files add seconds of transfer per shot. Small Fine JPEG is faster with no visible quality loss on prints.'
                        )}
                      {risk.reason === 'card-target' &&
                        t(
                          'cameraSettings.shutterRisk.cardTarget',
                          'Capturing to the memory card adds card-write latency per shot. "Internal RAM" is the faster tethered setting.'
                        )}
                      {findSectionContaining(config, risk.settingName) && (
                        <button
                          type="button"
                          onClick={() =>
                            revealConfigSection(
                              cameraKey,
                              findSectionContaining(config, risk.settingName) as string
                            )
                          }
                          className="ml-2 font-medium text-red-800 underline hover:text-red-900"
                        >
                          {t('cameraSettings.shutterRisk.jump', 'Fix in Camera Configuration ↓')}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {settings.useShutterCapture && (
              <div className={fieldClass}>
                <label className={fieldTitleClass}>
                  {t(
                    'cameraSettings.autoBrightenLiveViewForFlash.title',
                    'Brighten Live Preview When Using Flash'
                  )}
                </label>
                <div className={rowClass}>
                  <div className={rowTextClass}>
                    <span className={rowLeadClass}>
                      {t(
                        'cameraSettings.autoBrightenLiveViewForFlash.description',
                        'Automatically adjust camera settings (shutter speed) during live view to make the preview brighter when using flash. Original settings are restored before capture.'
                      )}
                    </span>
                    <p className="text-xs text-gray-500">
                      {t(
                        'cameraSettings.autoBrightenLiveViewForFlash.note',
                        'Only works with cameras that support shutter speed adjustment. Webcams are not supported.'
                      )}
                    </p>
                    {((isPrimary && !availableSettings?.isSupported) ||
                      (!isPrimary && !secondaryAvailableSettings?.isSupported)) && (
                      <p className="text-xs text-amber-600">
                        {t(
                          'cameraSettings.autoBrightenLiveViewForFlash.notSupported',
                          'Camera does not support automatic shutter speed adjustment'
                        )}
                      </p>
                    )}
                  </div>
                  <label className={rowControlClass}>
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={settings.autoBrightenLiveViewForFlash}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          autoBrightenLiveViewForFlash: e.target.checked
                        }))
                      }
                      aria-label={t(
                        'cameraSettings.autoBrightenLiveViewForFlash.toggle',
                        'Toggle brighten live view for flash'
                      )}
                    />
                    <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>

                {settings.autoBrightenLiveViewForFlash && (
                  <div className={isPanel ? `col-span-2 ${fieldClass} py-2` : 'mt-2 p-3 border border-gray-200 rounded-md bg-gray-50'}>
                    <label className={isPanel ? fieldTitleClass : 'block text-xs text-gray-600 mb-2'}>
                      {t(
                        'cameraSettings.liveViewShutterSpeedOverride.title',
                        'Live View Shutter Speed (optional)'
                      )}
                    </label>
                    <p className={isPanel ? fieldHintClass : 'text-xs text-gray-500 mb-2'}>
                      {t(
                        'cameraSettings.liveViewShutterSpeedOverride.description',
                        'Default: 1/30s. Slower speeds = brighter preview but more motion blur. Must be between 1/4s and 1/200s (flash sync speed).'
                      )}
                    </p>
                    <select
                      value={
                        settings.liveViewShutterSpeedOverride
                          ? `1/${Math.round(1 / settings.liveViewShutterSpeedOverride)}`
                          : '1/30'
                      }
                      onChange={(e) => {
                        const value = e.target.value
                        if (value === '1/30') {
                          setSettings((prev) => ({
                            ...prev,
                            liveViewShutterSpeedOverride: undefined
                          }))
                        } else {
                          const [num, den] = value.split('/').map(Number)
                          if (!isNaN(num) && !isNaN(den) && den !== 0) {
                            setSettings((prev) => ({
                              ...prev,
                              liveViewShutterSpeedOverride: num / den
                            }))
                          }
                        }
                      }}
                      className={isPanel ? selectClass : 'w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm'}
                      title={t(
                        'cameraSettings.liveViewShutterSpeedOverride.title',
                        'Live View Shutter Speed'
                      )}
                    >
                      <option value="1/4">{t('cameraSettings.shutterSpeed.1_4')}</option>
                      <option value="1/8">{t('cameraSettings.shutterSpeed.1_8')}</option>
                      <option value="1/15">{t('cameraSettings.shutterSpeed.1_15')}</option>
                      <option value="1/30">{t('cameraSettings.shutterSpeed.1_30')}</option>
                      <option value="1/60">{t('cameraSettings.shutterSpeed.1_60')}</option>
                      <option value="1/125">{t('cameraSettings.shutterSpeed.1_125')}</option>
                      <option value="1/200">{t('cameraSettings.shutterSpeed.1_200')}</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {showFlashGuide && (
              <div className="border-l-4 border-amber-400 bg-amber-50 p-4">
                <h4 className="text-sm font-semibold text-amber-800">
                  {t(
                    'cameraSettings.flashSetupGuide.title',
                    'Using flash? These camera-menu settings are required'
                  )}
                </h4>
                <p className="mt-1 text-xs text-amber-700">
                  {t(
                    'cameraSettings.flashSetupGuide.description',
                    'These settings live in the camera’s own menu and cannot be changed by the app. In live view, cameras only fire a flash they recognize as a dedicated TTL unit — with a manual single-pin flash (e.g. Godox TT520) the camera stays in silent/electronic mode and the flash never fires.'
                  )}
                </p>
                <div className="mt-2 space-y-1">
                  <details open={flashGuideBrand === 'canon'}>
                    <summary className="cursor-pointer text-xs font-semibold text-amber-800">
                      {t('cameraSettings.flashSetupGuide.canonDslr.title', 'Canon DSLR (1500D / 2000D class)')}
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-1 text-xs text-amber-700">
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonDslr.silentLv',
                          'Live View shooting menu → Silent LV shoot → Disable. This is the key fix — the flash will not fire in live view until it is off.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonDslr.expoSim',
                          'Exposure simulation → Disable, so the preview stays bright in a flash-dark room.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonDslr.flashFiring',
                          'Flash Control → Flash firing → Enable (it silently persists as Disabled).'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonDslr.mf',
                          'Lens to MF, pre-focused at guest distance — in a dark room autofocus fails and blocks the shutter.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonDslr.exposure',
                          'Manual exposure at or below sync speed: 1/125–1/160, f/7–f/8, ISO 100–200.'
                        )}
                      </li>
                    </ul>
                  </details>
                  <details open={flashGuideBrand === 'canon'}>
                    <summary className="cursor-pointer text-xs font-semibold text-amber-800">
                      {t('cameraSettings.flashSetupGuide.canonMirrorless.title', 'Canon mirrorless (R100, M50)')}
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-1 text-xs text-amber-700">
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonMirrorless.silentShutter',
                          'Silent Shutter Mode → Off, and Shutter mode → Mechanical or Electronic 1st-curtain. Full electronic shutter never fires flash.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonMirrorless.m50Firmware',
                          'M50: firmware 1.0.2 or newer (official Canon fix for unreliable hot-shoe flash communication).'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.canonMirrorless.expoSimMf',
                          'Exposure simulation → Disable; lens to MF.'
                        )}
                      </li>
                    </ul>
                  </details>
                  <details open={flashGuideBrand === 'nikon'}>
                    <summary className="cursor-pointer text-xs font-semibold text-amber-800">
                      {t('cameraSettings.flashSetupGuide.nikon.title', 'Nikon')}
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-1 text-xs text-amber-700">
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.nikon.silentLv',
                          'Photo Shooting Menu → Silent live view photography → Off.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.nikon.releasePriority',
                          'Custom settings a1/a2 (AF-C/AF-S priority) → Release, not Focus.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.nikon.mf',
                          'Manual focus via the lens switch (focus mode is read-only over USB on entry bodies).'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.nikon.popupAuto',
                          'Close the pop-up flash; mode dial off AUTO.'
                        )}
                      </li>
                    </ul>
                  </details>
                  <details open={flashGuideBrand === 'sony'}>
                    <summary className="cursor-pointer text-xs font-semibold text-amber-800">
                      {t('cameraSettings.flashSetupGuide.sony.title', 'Sony')}
                    </summary>
                    <ul className="mt-1 ml-4 list-disc space-y-1 text-xs text-amber-700">
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.sony.silent',
                          'Silent Shooting → Off, AND re-set e-Front Curtain Shutter manually — turning Silent off does not restore it.'
                        )}
                      </li>
                      <li>
                        {t('cameraSettings.flashSetupGuide.sony.pcRemote', 'USB mode → PC Remote.')}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.sony.releasePriority',
                          'Priority Set in AF-S → Release.'
                        )}
                      </li>
                      <li>
                        {t(
                          'cameraSettings.flashSetupGuide.sony.verify',
                          'Verify the flash menu is no longer greyed out.'
                        )}
                      </li>
                    </ul>
                  </details>
                </div>
                <p className="mt-2 text-xs text-amber-700">
                  {t(
                    'cameraSettings.flashSetupGuide.hardwareTip',
                    'Flash hardware: run manual speedlites at ≤ 1/2 power (full-power recycle takes up to 5 s on AA batteries — shots during recycle come out black). A TTL trigger on the hot shoe (e.g. Godox X1T) is detected by the camera and disables silent modes automatically.'
                  )}
                </p>
              </div>
            )}

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t('cameraSettings.useWebcam.title', 'Use Webcam')}
              </label>
              <div className={rowClass}>
                <div className={rowTextClass}>
                  {!isPanel && (
                  <span className={rowLeadClass}>
                    {t(
                      'cameraSettings.useWebcam.description',
                      'Enable webcam support for this app'
                    )}
                  </span>
                  )}
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.useWebcam.note',
                      'Disabling this will prevent webcam access and may help with camera conflicts'
                    )}
                  </p>
                </div>
                <label className={rowControlClass}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={useWebcam}
                    onChange={(e) => {
                      setUseWebcam(e.target.checked)
                      setTimeout(() => fetchDevices(), 100)
                    }}
                    aria-label={t('cameraSettings.useWebcam.toggle', 'Toggle webcam support')}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            <div className={fieldClass}>
              <label className={fieldTitleClass}>
                {t(
                  'cameraSettings.showCameraQualityWarnings.title',
                  'Show Camera Quality Warnings'
                )}
              </label>
              <div className={rowClass}>
                <div className={rowTextClass}>
                  <span className={rowLeadClass}>
                    {t(
                      'cameraSettings.showCameraQualityWarnings.description',
                      'Automatically show warnings when camera detects dark images, black images, or poor quality. Disable this if your photobooth room is intentionally dark before capture.'
                    )}
                  </span>
                  <p className="text-xs text-gray-500">
                    {t(
                      'cameraSettings.showCameraQualityWarnings.note',
                      'When disabled, quality warnings will not appear automatically'
                    )}
                  </p>
                </div>
                <label className={rowControlClass}>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showCameraQualityWarnings}
                    onChange={(e) => {
                      setShowCameraQualityWarnings(e.target.checked)
                    }}
                    aria-label={t(
                      'cameraSettings.showCameraQualityWarnings.toggle',
                      'Toggle camera quality warnings'
                    )}
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blueSparkle"></div>
                </label>
              </div>
            </div>

            {/* Last of the app-side settings, before the camera's own config tree: a
                once-per-booth choice, set by support for a booth whose uploads lag. The
                impact badge under Capture Image Quality points here while on PNG. */}
            <div className={fieldClass}>
              <label className={fieldTitleClass} htmlFor={`capture-format-${cameraKey}`}>
                {t('cameraSettings.captureFormat.title')}
              </label>
              <select
                id={`capture-format-${cameraKey}`}
                value={captureFormat}
                onChange={(e) =>
                  setSettings((prev) => ({
                    ...prev,
                    captureFormat: normaliseCaptureFormat(e.target.value)
                  }))
                }
                className={selectClass}
                title={t('cameraSettings.captureFormat.title')}
                aria-label={t('cameraSettings.captureFormat.title')}
              >
                {CAPTURE_FORMATS.map((format) => (
                  <option key={format} value={format}>
                    {t(`cameraSettings.captureFormat.options.${format}`)}
                  </option>
                ))}
              </select>
              <p className={fieldHintClass}>
                {t('cameraSettings.captureFormat.description')}{' '}
                {t(`cameraSettings.captureFormat.hint.${captureFormat}`)}
              </p>
            </div>

        {config && (
          <div>
            <h3 className="text-xl font-semibold mb-4">
              {t('cameraSettings.cameraConfig', 'Camera Configuration')}
            </h3>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('cameraSettings.cameraConfigWarning.title', 'Camera Configuration Warning')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {t(
                        'cameraSettings.cameraConfigWarning.description',
                        'Camera settings may vary between different camera models. Some settings might not be adjustable through this software. Please test each setting to ensure it works with your camera.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              {configSections.length > 0 ? (
                // Render top-level sections individually so each gets a stable DOM
                // anchor for the jump nav in the sticky header (and the risk banner's
                // "Fix in Camera Configuration" links).
                configSections.map(([sectionName, section]) => (
                  <div key={sectionName} id={cameraConfigSectionId(cameraKey, sectionName)}>
                    <CameraConfigWidget
                      config={section}
                      onChange={(name, value) => handleCameraConfigChange(cameraKey, name, value)}
                      layout={isPanel ? 'row' : 'stacked'}
                    />
                  </div>
                ))
              ) : (
                <CameraConfigWidget
                  config={config}
                  onChange={(name, value) => handleCameraConfigChange(cameraKey, name, value)}
                  layout={isPanel ? 'row' : 'stacked'}
                />
              )}
            </div>
          </div>
        )}
        </SettingsDisclosure>
          </div>
        </div>
        {/* Multi-project hub: bind THIS camera to the project on screen.
            Local on purpose — it names hardware only this booth has, so it
            belongs beside the printer binding in PrinterSettingsModal and not
            in the hub, which is shared by booths with different cameras.
            A hub never captures, so it is never bindable. */}
        {inHub && activeProjectId && !activeProjectIsHub && (
          <div className={isPanel ? 'mt-3' : 'mt-3 p-3 border border-gray-200 rounded-md'}>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="project-camera-binding"
                checked={cameraSlotByProject?.[activeProjectId] === cameraKey}
                onChange={() => setCameraSlotForProject(activeProjectId, cameraKey)}
              />
              {t(
                'settings.camera.projectBinding.useForProject',
                'Use this camera for the active project: {{title}}',
                { title: activeProjectTitle || activeProjectId }
              )}
            </label>
            {cameraSlotByProject?.[activeProjectId] === cameraKey && (
              <div className="mt-2 flex items-center gap-3 text-xs">
                {!selectedCam && (
                  <span className="text-amber-600">
                    {t(
                      'settings.camera.projectBinding.noCamera',
                      'No camera is selected on this slot - the booth falls back to its usual choice.'
                    )}
                  </span>
                )}
                <button
                  type="button"
                  className="text-blue-600 underline"
                  onClick={() => setCameraSlotForProject(activeProjectId, undefined)}
                >
                  {t('settings.camera.projectBinding.unbind', 'Remove binding')}
                </button>
              </div>
            )}
            <div className="mt-1 text-xs text-gray-500">
              {t(
                'settings.camera.projectBinding.help',
                'The guest is no longer asked which camera to use for this project.'
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (isPanel) {
    // General settings first (the pane's picker, via `leading`), then the slot tabs,
    // then the active slot's own settings. The right column is the section's sticky
    // workbench: the live preview with the test shutter on it, plus helpers.
    return (
      <div
        className={
          twoColumn
            ? 'grid grid-cols-[minmax(0,32rem)_minmax(16rem,1fr)] items-start gap-6'
            : 'flex flex-col gap-4'
        }
      >
        <div className="min-w-0 space-y-4">
          {leading}
          {/* Tabs + the active slot's picker stay pinned while the settings scroll, so the
              operator can switch slot or camera from any scroll position and land at the
              same point. `pinHeader` only ever unpins in the standalone modal. */}
          <div
            id={`camera-settings-sticky-${activeTab}`}
            className={`border-b border-gray-100 bg-white pb-3 ${pinHeader ? 'sticky top-0 z-20' : ''}`}
          >
          <div className="custom-scrollbar mb-3 flex overflow-x-auto border-b border-gray-200">
            {(['primary', 'secondary'] as CameraKey[]).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`shrink-0 whitespace-nowrap px-4 py-2 text-sm font-medium ${
                  activeTab === key
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t(key === 'primary' ? 'cameraSettings.primary' : 'cameraSettings.secondary')}
              </button>
            ))}
          </div>
          {renderCameraSettings(activeTab, 'picker')}
          </div>
          {renderCameraSettings(activeTab)}
        </div>
        <div className={twoColumn ? 'sticky top-4 space-y-3 self-start' : 'order-first space-y-3'}>
          {renderCameraSettings(activeTab, 'preview')}
          {aside}
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4 sm:p-6">
      {/* Height comes from the overlay (percent of the fixed containing block), NOT dvh:
          on custom-path booths this modal renders inside ScaleToFit's CSS transform, where
          the containing block is the design canvas but dvh still measures the physical
          window — on a small monitor that mismatch shrank the modal to a fraction of the
          visible canvas. Percent tracks the right box in both shells. `container-type`
          makes the box the reference for the preview's cqh height budget. */}
      <div
        className="flex h-full max-h-full w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        style={{ containerType: 'size' }}
      >
        {/* Fixed header (title + tabs). Kept OUT of the scroll area so the sticky camera header
            below sits flush at the scroll top — no top-padding gap for content to peek through. */}
        <div className={`shrink-0 ${camModalDense ? 'px-3 pt-3' : 'px-4 pt-4 sm:px-8 sm:pt-6'}`}>
          <h2
            className={`mb-3 font-bold ${camModalDense ? 'text-base' : 'text-lg sm:mb-5 sm:text-2xl'}`}
          >
            {t('cameraSettings.title', 'Camera Settings')}
          </h2>

          <div className="flex border-b border-gray-200">
            <button
              type="button"
              onClick={() => setActiveTab('primary')}
              className={`font-medium ${camModalDense ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} ${
                activeTab === 'primary'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('cameraSettings.primary')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('secondary')}
              className={`font-medium ${camModalDense ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'} ${
                activeTab === 'secondary'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('cameraSettings.secondary')}
            </button>
          </div>
        </div>

        <div
          ref={scrollAreaRef}
          {...{ [CAMERA_SETTINGS_SCROLL_ATTR]: '' }}
          className={`flex-grow overflow-y-auto ${
            camModalDense
              ? 'px-3 pb-3 [&_.mb-4]:!mb-2 [&_.mb-6]:!mb-3 [&_.mt-2]:!mt-1 [&_.space-y-4]:!space-y-2 [&_.p-4]:!p-2.5 [&_h3]:!mb-2 [&_h3]:!text-sm [&_h3]:!font-semibold [&_label]:!mb-1 [&_label]:!text-[11px] [&_select]:!py-1.5 [&_select]:!text-xs [&_input]:!py-1.5 [&_input]:!text-xs [&_.text-xl]:!text-base [&_.text-sm]:!text-xs'
              : 'px-4 pb-4 sm:px-8 sm:pb-8'
          }`}
        >
          {activeTab === 'primary' ? renderCameraSettings('primary') : null}
          {activeTab === 'secondary' ? renderCameraSettings('secondary') : null}
        </div>

        <div
          className={`flex shrink-0 justify-end gap-2 border-t border-gray-200 bg-white px-4 sm:gap-3 ${camModalDense ? 'py-2' : 'py-3 sm:py-4'}`}
        >
          <Button
            text={t('common.cancel', 'Cancel')}
            variant="secondary"
            onClick={closeWithSnapshot}
            className={camModalDense ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm sm:!px-6 sm:!text-base'}
          />
          <Button
            text={t('common.save', 'Save')}
            onClick={() => {
              setCameraSettings(tempSettings)
              setSecondaryCameraSettings(tempSecondarySettings)
              closeWithSnapshot()
            }}
            className={camModalDense ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm sm:!px-6 sm:!text-base'}
          />
        </div>
      </div>
    </div>
  )
}

export default CameraSettingsModal
